/**
 * Entitlement Context
 * Manages authorization layer separate from authentication
 * Checks entitlements AFTER user is authenticated
 */

import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type {
  EntitlementCheckResult,
  AccessLevel,
  Organization
} from '../types/entitlement';
import { entitlementService } from '../services/entitlementService';
import { useAuth } from './AuthContext';

interface EntitlementContextType {
  // Entitlement state
  entitlements: EntitlementCheckResult | null;
  isLoading: boolean;
  error: string | null;
  
  // Access information
  accessLevel: AccessLevel;
  hasAccess: boolean;
  isTrial: boolean;
  isGracePeriod: boolean;
  isTeamLicense: boolean;
  daysRemaining?: number;
  
  // Feature access
  canAccessFeature: (requiredLevel: AccessLevel) => boolean;
  hasFeature: (featureId: string) => boolean;
  
  // Entitlement actions
  startTrial: () => Promise<void>;
  startDemo: () => Promise<void>;
  grantCurriculumAccess: (courseId: string, courseName: string, durationDays: number) => Promise<void>;
  refreshEntitlements: () => Promise<void>;
  
  // Organization
  organization?: Organization;
  isOrganizationMember: boolean;
}

const EntitlementContext = createContext<EntitlementContextType | undefined>(undefined);

interface EntitlementProviderProps {
  children: ReactNode;
}

export function EntitlementProvider({ children }: EntitlementProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [entitlements, setEntitlements] = useState<EntitlementCheckResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organization] = useState<Organization | undefined>();
  // setOrganization will be used when organization fetching is implemented
  
  // Load entitlements when user authenticates
  useEffect(() => {
    if (isAuthenticated && user) {
      loadEntitlements();
    } else {
      // Not authenticated - set to free tier
      setEntitlements({
        hasAccess: true,
        accessLevel: 'free',
        activeEntitlements: [],
        isGracePeriod: false,
        isTrial: false,
        isTeamLicense: false
      });
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);
  
  const loadEntitlements = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Check entitlements through service
      const result = await entitlementService.checkEntitlements(user.id);
      setEntitlements(result);
      
      // Load organization if team license
      if (result.isTeamLicense && result.organizationId) {
        // In production, fetch organization details
        // const org = await organizationService.getOrganization(result.organizationId);
        // setOrganization(org);
      }
      
    } catch (err) {
      console.error('Failed to load entitlements:', err);
      setError('Failed to load entitlements');
      
      // Fail open to free tier
      setEntitlements({
        hasAccess: true,
        accessLevel: 'free',
        activeEntitlements: [],
        isGracePeriod: false,
        isTrial: false,
        isTeamLicense: false,
        warnings: ['Error loading entitlements. Using free tier.']
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const canAccessFeature = (requiredLevel: AccessLevel): boolean => {
    if (!entitlements) return false;
    
    const levels: Record<AccessLevel, number> = {
      free: 0,
      basic: 1,
      pro: 2,
      business: 3,
      enterprise: 4,
      unlimited: 5
    };
    
    return levels[entitlements.accessLevel] >= levels[requiredLevel];
  };
  
  const hasFeature = (featureId: string): boolean => {
    if (!entitlements || !entitlements.primaryEntitlement) return false;
    
    const features = entitlements.primaryEntitlement.metadata.features || [];
    return features.includes(featureId);
  };
  
  const startTrial = async () => {
    if (!user) {
      throw new Error('Must be authenticated to start trial');
    }
    
    try {
      setIsLoading(true);
      await entitlementService.startTrial(user.id);
      await loadEntitlements();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start trial';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const startDemo = async () => {
    if (!user) {
      throw new Error('Must be authenticated to start demo');
    }
    
    try {
      setIsLoading(true);
      await entitlementService.startDemo(user.id);
      await loadEntitlements();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start demo';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const grantCurriculumAccess = async (
    courseId: string,
    courseName: string,
    durationDays: number
  ) => {
    if (!user) {
      throw new Error('Must be authenticated to grant curriculum access');
    }
    
    try {
      setIsLoading(true);
      await entitlementService.grantCurriculumAccess(
        user.id,
        courseId,
        courseName,
        durationDays
      );
      await loadEntitlements();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to grant curriculum access';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const refreshEntitlements = async () => {
    await loadEntitlements();
  };
  
  const value: EntitlementContextType = {
    entitlements,
    isLoading,
    error,
    accessLevel: entitlements?.accessLevel || 'free',
    hasAccess: entitlements?.hasAccess || false,
    isTrial: entitlements?.isTrial || false,
    isGracePeriod: entitlements?.isGracePeriod || false,
    isTeamLicense: entitlements?.isTeamLicense || false,
    daysRemaining: entitlements?.daysRemaining,
    canAccessFeature,
    hasFeature,
    startTrial,
    startDemo,
    grantCurriculumAccess,
    refreshEntitlements,
    organization,
    isOrganizationMember: !!organization
  };
  
  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlements(): EntitlementContextType {
  const context = useContext(EntitlementContext);
  if (context === undefined) {
    throw new Error('useEntitlements must be used within an EntitlementProvider');
  }
  return context;
}

// Convenience hook for feature access
export function useFeatureAccess(requiredLevel: AccessLevel): boolean {
  const { canAccessFeature } = useEntitlements();
  return canAccessFeature(requiredLevel);
}

// Convenience hook for specific feature check
export function useHasFeature(featureId: string): boolean {
  const { hasFeature } = useEntitlements();
  return hasFeature(featureId);
}
